/**
 * Runway and taxiway closures recorded on the Interactive Map, carried to the
 * platform's closure register.
 *
 * The map records a closure the way the officer reads it off the NOTAM: which
 * surface, the NOTAM number, from when to when, and why. The platform records
 * a closure against a zone, planned or unplanned, in force or waiting to
 * start. This file is the one place that turns the first into the second.
 *
 * Three things it is careful about:
 *   - A closure that starts later is sent as PLANNED with its planned start.
 *     Sent as unplanned, the platform would close the surface now.
 *   - The window is the NOTAM's own, so the closure is marked to start and
 *     end on the clock (`ends_at_planned_end`); the platform's lapse job then
 *     opens and closes it on time.
 *   - Nobody's name is sent. The platform stamps who recorded and who lifted.
 *
 * Imports nothing from the demo domain: this sits in the entry chunk.
 */
import { PERMISSION } from "../../../api/dataSource";
import { surfaceCode, type DeclareClosureInput } from "../../../domain/closures";
import { t } from "../../../i18n";
import { registerWrite, type RecordStamp } from "../writes";

/** A closure as the map's engine holds it. */
export interface MapClosureRecord {
  /** the NOTAM number */
  id: string;
  kind: "runway" | "taxiway";
  name: string;
  reason?: string;
  from: string;
  to: string | null;
  by?: string;
  /** planned works or an unplanned closure, as the officer chose */
  plan?: "planned" | "unplanned";
  sample?: boolean;
  /** how this browser's copy reached the platform */
  platform?: RecordStamp;
  /** the platform's id, on a closure read back from the register */
  platformId?: string;
  lifted?: { at: string; by: string; platform?: RecordStamp };
}

/** A start this close to now is now: the officer is closing the surface as they type. */
const NOW_SLACK_MS = 60_000;

const label = (r: Pick<MapClosureRecord, "kind" | "name">) => `${r.kind === "runway" ? "RWY" : "TWY"} ${r.name}`;

/** The closure the platform is asked to record, from the map's own record. */
export function closureBody(rec: MapClosureRecord, now = Date.now()): Omit<DeclareClosureInput, "siteId"> {
  const later = Date.parse(rec.from) > now + NOW_SLACK_MS;
  return {
    zoneCode: surfaceCode(rec.kind, rec.name),
    reason: (rec.reason ?? "").trim(),
    /* a closure that has not begun can only be planned */
    kind: later ? "planned" : (rec.plan ?? "unplanned"),
    reference: rec.id.trim(),
    ...(later ? { plannedStart: rec.from } : { startedAt: rec.from }),
    plannedEnd: rec.to ?? null,
    attrs: {
      notam: rec.id.trim(),
      surface_kind: rec.kind,
      surface: rec.name,
      source: "interactive_map",
      ends_at_planned_end: true,
    },
  };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * The platform's id for a closure, waiting while its own save is still on the
 * way — a closure lifted a moment after it was recorded is lifted once the
 * platform has it, not refused for being early.
 */
export async function platformIdOf(
  c: MapClosureRecord | undefined,
  wait: (ms: number) => Promise<unknown> = sleep,
  patienceMs = 20_000,
): Promise<string | undefined> {
  if (!c) return undefined;
  for (let waited = 0; c.platform?.state === "saving" && waited < patienceMs; waited += 250) await wait(250);
  return c.platformId ?? (c.platform?.state === "saved" ? c.platform.id : undefined);
}

const refresh = (siteId: string) => [["closures", siteId]];

registerWrite("closures.declare", {
  permission: () => PERMISSION.closureWrite,
  invalidate: refresh,
  /* POST /v1/closures honours Idempotency-Key */
  retryable: true,
  async send(ctx, rec: MapClosureRecord) {
    const ack = await ctx.source.declareClosure({ siteId: ctx.siteId, ...closureBody(rec) }, ctx.key);
    const vars = { surface: label(rec), notam: rec.id };
    return {
      id: ack.id,
      reference: rec.id,
      ...(ack.startedAt ? { at: ack.startedAt } : {}),
      text:
        ack.status === "planned"
          ? t("{surface} closure under {notam} saved on the platform. It starts on time.", vars)
          : t("{surface} closed under {notam}. Saved on the platform.", vars),
    };
  },
});

registerWrite("closures.lift", {
  permission: () => PERMISSION.closureWrite,
  invalidate: refresh,
  /* POST /v1/closures/{id}/end honours Idempotency-Key */
  retryable: true,
  async send(ctx, _lifted, extra: { closure?: MapClosureRecord } | null) {
    const c = extra?.closure;
    const id = await platformIdOf(c);
    if (!c || !id) throw new Error(t("This closure is not on the platform, so it cannot be lifted there."));
    const ack = await ctx.source.endClosure(id, undefined, ctx.key);
    return {
      id,
      reference: c.id,
      at: ack.endedAt,
      text: t("{surface} reopened. {notam} lifted on the platform.", { surface: label(c), notam: c.id }),
    };
  },
});
